import { useEffect } from 'react';
import { cancelAdjustment } from '../../app/adjust';
import { appCommands, appContext } from '../../app/commands';
import { isEditableTarget } from '../../core/input/shortcuts';
import { useEditorStore } from '../../state/editorStore';

/**
 * Global keyboard handling.
 *
 * Order of responsibility:
 *  1. Escape always closes overlays, even from text fields.
 *  2. Keys typed inside inputs/contenteditable are never treated as shortcuts.
 *  3. Everything else goes to the command registry, which owns the bindings.
 */
export function useGlobalShortcuts(): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const state = useEditorStore.getState();

      if (event.key === 'Escape') {
        // Overlays own Escape, in priority order, and swallow it: Escape should
        // never both close a dialog and deselect behind it.
        if (state.paletteOpen) {
          state.setPaletteOpen(false);
          return;
        }
        if (state.showShortcuts) {
          state.setShowShortcuts(false);
          return;
        }
        if (state.showAbout) {
          state.setShowAbout(false);
          return;
        }
        if (state.resizeDialog !== null) {
          state.setResizeDialog(null);
          return;
        }
        if (state.exportDialogOpen) {
          state.setExportDialog(false);
          return;
        }
        if (state.confirm) return;
        // An adjustment preview is the most transient thing on screen: Escape
        // throws the look away rather than deselecting behind it.
        if (state.adjustment) {
          cancelAdjustment(appContext);
          event.preventDefault();
          return;
        }
        // Then the registry (deselect); a toast only gets Escape if nothing
        // else wanted it, otherwise a fresh toast would eat the key.
        void appCommands.runShortcut(event, appContext).then((handled) => {
          if (handled) event.preventDefault();
          else if (state.toast) state.dismissToast();
        });
        return;
      }

      if (isEditableTarget(event.target)) return;
      if (event.repeat) return;
      // A widget that already claimed the key (the curve editor's arrows, for
      // instance) prevents the default itself; global shortcuts stand down.
      if (event.defaultPrevented) return;

      void appCommands.runShortcut(event, appContext).then((handled) => {
        if (handled) event.preventDefault();
      });
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
